import { Alert, Collapse, Divider } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { ICustomControlRequestAddEditProps } from '../../interfaces/';
import { getServiceLabel } from '../../../../../../common/customControls/getServiceLabel';
import { ProcessEntity } from '../../../../../../common/enums/processEntity';
import { ToastType } from '../../../../../../common/enums/toastType';
import { YesOrNo } from '../../../../../../common/enums/yesOrNo';
import { getDisplayName } from '../../../../../../common/utils/getDisplayName';
import { dermaOthV1FinalRequest } from '../../../../../../common/validation/customControls/dermatology';
import { fetchJson } from '../../../../../store/fetch';
import { translateKey } from '../../../../../translate';
import { useEpisodeContext } from '../../../../../utils/hooks/useEpisodeContext';
import { RateLimiter } from '../../../../../utils/rateLimiter';
import { showServiceToast } from '../../../../../utils/showToast';
import { CancelButton } from '../../../../elements/carePathButtons/CancelButton';
import { SaveButton } from '../../../../elements/carePathButtons/SaveButton';
import { SaveSendButton } from '../../../../elements/carePathButtons/SaveSendButton';
import { ButtonContainer, Form, FormParagraph, HeaderDivider, InputText } from '../../../../elements/common';
import { FormContextProvider } from '../../../../elements/formContext';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { FormSection } from '../../../../elements/forms/formSection/FormSection';
import { HorizontalFormGroup } from '../../../../elements/forms/HorizontalFormGroup';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { InputControl } from '../../../../elements/forms/inputControl/InputControl';
import { List } from '../../../../elements/List/List';
import { PatientContactAddEdit } from '../../../../elements/PatientContactAddEdit';
import { CheckboxField } from '../../../../fields/Checkbox';
import { RadioGroupField } from '../../../../fields/RadioGroup';
import { CareProviderSelector } from '../../utils/v1/CareProviderSelector';
import { ExtendPermission } from '../../utils/v1/ExtendPermission';
import { GraderSelector } from '../../utils/v1/GraderSelector';
import { NewOrEditRequestProcessContainer } from '../../utils/v1/NewOrEditRequestProcessContainer';
import { DermaOthV1FollowingSteps } from './DermaOthV1FollowingSteps';

export interface IDermaOthV1RequestCustomControlData {
    contactgegevens_patient: {
        emailadres: string | null;
    };
    identifierSocialSecurity: string | null;
    invitationSentAt?: string | Date;
    patientHasDigid: YesOrNo | null;
    toestemming: boolean;
}

export function DermaOthV1RequestAddEdit(props: ICustomControlRequestAddEditProps) {
    const { patient } = useEpisodeContext();
    const rateLimiter = new RateLimiter(500);
    const [buttonDisabled, setButtonDisabled] = useState(false);
    const [showDetails, setShowDetails] = useState(false);
    const [showBsnInput, setShowBsnInput] = useState(false);
    const [bsnIsDuplicate, setBsnIsDuplicate] = useState(false);

    useEffect(() => {
        if (patient.identifierSocialSecurity) {
            props.handleChangeCustomControl({ identifierSocialSecurity: patient.identifierSocialSecurity });
        } else {
            setShowBsnInput(true);
            setButtonDisabled(true);
        }
    }, [patient]);

    return (
        <NewOrEditRequestProcessContainer
            isExaminationRequest
            isFirstItem={props.isFirstItem}
            itemState={props.itemState}
            process={props.process}
            service={props.service}
            title={getServiceLabel(props.service.code, 'request', props.service.name)}
        >
            {addEditForm()}
        </NewOrEditRequestProcessContainer>
    );

    function addEditForm() {
        return (
            <Form onSubmit={async () => null}>
                <CareProviderSelector
                    {...props}
                    entity={props.request}
                />

                <GraderSelector
                    {...props}
                    careProviderAndRequesterAndSelectedProvider={{
                        ...props.request,
                        requesterOrganizationId: props.organizationId,
                    }}
                    procedurePerformerOrganizationId={props.organizationId}
                />

                <FormParagraph>
                    Je kunt alleen een thuisconsult starten als de patiënt in staat is om zelf foto's te uploaden via
                    mobiel, tablet of computer.
                </FormParagraph>

                <FormContextProvider
                    value={{
                        placeholderGroupName: 'request',
                        item: props.request.customControlData,
                        errors: props.errors,
                        customControlData: true,
                        onChange: async (key, value) => await handleChangeCustomControl({ [key]: value }),
                        onDoValidate: async (key) => await props.validateOneCustomControl(key),
                    }}
                >
                    <HeaderDivider text={translateKey('patient.detailsCounselling')} />

                    {showBsnInput && (
                        <HorizontalFormGroup hasErrors={props.errors.has('customControlData.identifierSocialSecurity')}>
                            <FormLabel isRequired>{translateKey('patient.identifierSocialSecurity')}</FormLabel>
                            <InputContainer>
                                <InputText
                                    automation="identifierSocialSecurity"
                                    error={props.errors.get('customControlData.identifierSocialSecurity')}
                                    onChange={async (value) =>
                                        await handleChangeCustomControl({ identifierSocialSecurity: value })
                                    }
                                    onDoValidate={async () =>
                                        await props.validateOneCustomControl('identifierSocialSecurity')
                                    }
                                    placeholder="Voer het BSN in..."
                                    value={props.request.customControlData.identifierSocialSecurity}
                                />
                            </InputContainer>
                        </HorizontalFormGroup>
                    )}

                    {bsnIsDuplicate && (
                        <Alert appearance="warning">
                            Dit BSN is al geregistreerd bij een andere patiënt. Weet je zeker dat je het juiste BSN hebt
                            ingevuld? Bel dan Ksyos (020 600 00 60) om dit op te lossen.
                        </Alert>
                    )}

                    <PatientContactAddEdit
                        hideHeader
                        showEmailOnly
                        {...props}
                    />

                    <RadioGroupField
                        fieldKey="patientHasDigid"
                        isRequired
                        label="Heeft de patiënt een DigiD?"
                        labels={[translateKey('common.yes'), translateKey('common.no')]}
                        options={[YesOrNo.yes, YesOrNo.no]}
                    />

                    {props.request.customControlData?.patientHasDigid === YesOrNo.no && (
                        <Alert appearance="info">
                            We sturen de patiënt een e-mail met een link naar het Thuisconsult. Deze link blijft 24 uur
                            geldig.{' '}
                        </Alert>
                    )}

                    {props.request.customControlData?.patientHasDigid === YesOrNo.yes && (
                        <Alert appearance="info">
                            We sturen de patiënt een e-mail met een link naar het Thuisconsult. De patiënt logt daarop
                            in met DigiD.{' '}
                        </Alert>
                    )}

                    <Divider />

                    <FormSection type="referral">
                        <HeaderDivider text="Toestemming voor mogelijk consult" />
                        <InputControl isFormControl>
                            <CheckboxField
                                fieldKey="toestemming"
                                label=""
                                text="De patiënt geeft toestemming voor een dermatologie consult indien de huisarts dit nodig acht."
                            />
                            <Collapse isOpen={showDetails}>
                                <List
                                    hasBullets
                                    items={[
                                        <span>Waar patiënt staat bedoelen wij de patiënt en/of gemachtigde.</span>,
                                        <span>
                                            <b>Eigen risico</b> : De kosten voor deze zorg kunnen mee tellen voor het
                                            eigen risico, afhankelijk van de zorgverzekeraar van de patiënt.{' '}
                                        </span>,
                                        <span>
                                            <b>Gegevens</b> : Relevante medische gegevens worden gedeeld met betrokken
                                            zorgverleners en zorgorganisaties.
                                        </span>,
                                    ]}
                                />
                            </Collapse>

                            <ExtendPermission
                                onClick={() => setShowDetails(!showDetails)}
                                show={showDetails}
                            />
                        </InputControl>
                    </FormSection>
                </FormContextProvider>

                <DermaOthV1FollowingSteps />

                <ButtonContainer
                    alignRight
                    buttons={[
                        <CancelButton
                            cancel={props.onCancel}
                            entity={ProcessEntity.request}
                            isExisting={!!props.request.id}
                            serviceCode={props.service.code}
                            serviceType={props.service.serviceType}
                        />,
                        <SaveButton
                            buttonDisabled={buttonDisabled}
                            save={async () => {
                                await saveRequest('draft');
                            }}
                        />,
                        <SaveSendButton
                            buttonDisabled={buttonDisabled}
                            saveSend={async () => {
                                await saveRequest('final');
                            }}
                        />,
                    ]}
                />
            </Form>
        );
    }

    async function handleChangeCustomControl(data: Partial<IDermaOthV1RequestCustomControlData>) {
        if ('identifierSocialSecurity' in data) {
            await checkBsn(data.identifierSocialSecurity);
        }
        await props.handleChangeCustomControl(data);
    }

    async function saveRequest(saveType: 'draft' | 'final') {
        setButtonDisabled(true);

        const validatedRequest = await props.validateRequest(saveType);
        if (!validatedRequest) {
            setButtonDisabled(false);

            return;
        }

        const requestId = await props.onSaveRequest(saveType);

        if (saveType === 'final') {
            showServiceToast(
                ToastType.success,
                undefined,
                props.service,
                'request',
                `Het onderzoek is verstuurd naar ${getDisplayName(patient)}. Volg de status via je takenlijst.`
            );
        } else {
            showServiceToast(
                ToastType.success,
                'toast.draftSuccess',
                props.service,
                'request',
                translateKey('toast.draftDefaultMessage')
            );
        }
        if (props.reloadPatient) {
            await props.reloadPatient();
        }
        await props.onSave(requestId);
    }

    async function checkBsn(bsn: string | null | undefined) {
        setButtonDisabled(true);
        setBsnIsDuplicate(false);

        const { error } = dermaOthV1FinalRequest.validate({ identifierSocialSecurity: bsn });
        if (error) {
            return;
        }
        rateLimiter.callMeMaybe(async () => {
            const data = { identifierSocialSecurity: bsn };
            const result = await fetchJson('/api/patients/duplicates', 'POST', data);
            if (result.json.count === 0) {
                setButtonDisabled(false);
            } else {
                setBsnIsDuplicate(true);
            }
        });
    }
}

export function dermaOthV1RequestInitializeCustomControlData(): IDermaOthV1RequestCustomControlData {
    return {
        contactgegevens_patient: {
            emailadres: null,
        },
        toestemming: false,
        patientHasDigid: null,
        identifierSocialSecurity: null,
    };
}
